import React, { useState, useEffect } from "react";
import { Offcanvas, Button, Table, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const RevenueBundleModal = ({ showModal, handleClose, deal, fetchDeals }) => {
    const [unbundledItems, setUnbundledItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // Calculate total invoice amount
    const totalInvoiceAmount = Number(
        deal?.total_value || deal?.po_value || deal?.quotation?.amount || 0
    );

    useEffect(() => {
        if (showModal && deal) {
            fetchExistingUnbundled();
        }
    }, [showModal, deal]);

    const fetchExistingUnbundled = async () => {
        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");
            const token = userInfo?.token;

            const res = await axios.get(`${backendUrl}/api/deals/${deal.id}/unbundle`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            if (res.data.items && res.data.items.length > 0) {
                const fetchedItems = res.data.items.map(item => ({
                    id: item.id,
                    name: item.name || "",
                    amount: item.amount || "",
                    start_date: item.start_date || "",
                    end_date: item.end_date || "",
                    payment_terms: item.payment_terms || "",
                    type: item.type || "",
                }));
                setUnbundledItems(fetchedItems);
            } else {
                setUnbundledItems([
                    {
                        id: Date.now(),
                        name: "",
                        amount: "",
                        start_date: "",
                        end_date: "",
                        payment_terms: "",
                        type: "",
                    },
                ]);
            }
        } catch (error) {
            console.error("Error fetching unbundled items:", error);
            setUnbundledItems([
                {
                    id: Date.now(),
                    name: "",
                    amount: "",
                    start_date: "",
                    end_date: "",
                    payment_terms: "",
                    type: "",
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        setUnbundledItems([
            ...unbundledItems,
            {
                id: Date.now(),
                name: "",
                amount: "",
                start_date: "",
                end_date: "",
                payment_terms: "",
                type: "",
            },
        ]);
    };

    const handleRemoveRow = (id) => {
        setUnbundledItems(unbundledItems.filter((item) => item.id !== id));
    };

    const handleChange = (id, field, value) => {
        const newItems = unbundledItems.map((item) => {
            if (item.id === id) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setUnbundledItems(newItems);
    };

    const currentTotalUnbundled = unbundledItems.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
    );

    const handleSave = async () => {
        if (currentTotalUnbundled !== totalInvoiceAmount) {
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: `Total unbundled amount (${currentTotalUnbundled}) must equal Invoice Amount (${totalInvoiceAmount}).`,
                timer: 4000,
                showConfirmButton: false,
            });
            return;
        }

        const hasEmptyNameOrAmount = unbundledItems.some(
            (item) => !item.name.trim() || !item.amount
        );
        if (hasEmptyNameOrAmount) {
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: "Please fill in Name and Amount for all items.",
                timer: 4000,
                showConfirmButton: false,
            });
            return;
        }

        try {
            setLoading(true);
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");
            const token = userInfo?.token;

            const payload = {
                deal_id: deal.id,
                items: unbundledItems,
            };

            await axios.post(`${backendUrl}/api/deals/${deal.id}/unbundle`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: "Revenue successfully unbundled!",
                timer: 3000,
                showConfirmButton: false,
            });

            if (fetchDeals) fetchDeals();
            handleClose();
        } catch (error) {
            console.error("Error saving unbundled items:", error);
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: error.response?.data?.message || "Failed to save unbundled items.",
                timer: 4000,
                showConfirmButton: false,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Offcanvas
            show={showModal}
            onHide={handleClose}
            placement="end"
            backdrop="static"
            style={{ width: '1150px', borderLeft: '1px solid rgba(0,0,0,0.1)' }}
        >
            <Offcanvas.Header
                closeButton
                className="border-bottom pb-2"
                style={{
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
                    padding: '1.5rem 2rem'
                }}
            >
                <div>
                    <Offcanvas.Title
                        as="h5"
                        style={{
                            fontWeight: '600',
                            fontSize: '1.4rem',
                            color: '#2c3e50',
                            marginBottom: '0.25rem'
                        }}
                    >
                        Bundle / Unbundle Revenue
                    </Offcanvas.Title>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                        Split the total invoice amount into separate performance obligations
                    </p>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body
                className="px-0"
                style={{
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d8e0 #f1f3f5'
                }}
            >
                <div className="px-4 pt-3 pb-2">
                    <div className="alert alert-info py-2 d-flex justify-content-between align-items-center mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
                        <div>
                            <strong>Order/Project:</strong> <span className="ms-1">{deal?.name || deal?.project?.name || "N/A"}</span>
                        </div>
                        <div>
                            <strong>Total Order:</strong>{" "}
                            <span className="fs-5 ms-1 fw-bold text-primary">{totalInvoiceAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>

                   
                    <div className="table-responsive shadow-sm" style={{ borderRadius: '8px', border: '1px solid #eef2f7' }}>
                        <Table hover className="align-middle mb-0" style={{ minWidth: '700px' }}>
                            <thead style={{ background: '#f8f9fa' }}>
                                <tr>
                                    <th style={{ width: "25%", fontWeight: '600', color: '#495057' }}>Item Name <span className="text-danger">*</span></th>
                                    <th style={{ width: "12%", fontWeight: '600', color: '#495057' }}>Type</th>
                                    <th style={{ width: "12%", fontWeight: '600', color: '#495057' }}>Amount <span className="text-danger">*</span></th>
                                    <th style={{ width: "15%", fontWeight: '600', color: '#495057' }}>Start Date</th>
                                    <th style={{ width: "15%", fontWeight: '600', color: '#495057' }}>End Date</th>
                                    <th style={{ width: "13%", fontWeight: '600', color: '#495057' }}>Payment Terms</th>
                                    <th style={{ width: "8%" }} className="text-center"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {unbundledItems.map((item) => (
                                    <tr key={item.id}>
                                        <td className="p-2">
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                value={item.name}
                                                onChange={(e) => handleChange(item.id, "name", e.target.value)}
                                                className="border-0 bg-light"
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </td>
                                        
                                        <td className="p-2">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                placeholder="0.00"
                                                value={item.amount}
                                                onChange={(e) => handleChange(item.id, "amount", e.target.value)}
                                                className="border-0 bg-light"
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </td>
                                        <td className="p-2">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    value={item.start_date ? dayjs(item.start_date) : null}
                                                    onChange={(newValue) => {
                                                        handleChange(item.id, "start_date", newValue ? newValue.format('YYYY-MM-DD') : "");
                                                    }}
                                                    format="DD-MM-YYYY"
                                                    openTo="year"
                                                    views={['year', 'month', 'day']}
                                                    slotProps={{ 
                                                        textField: { 
                                                            size: "small",
                                                            sx: {
                                                                width: '150px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: '6px',
                                                                    '& fieldset': { border: 'none' },
                                                                },
                                                                '& .MuiInputBase-input': { padding: '6px 8px', fontSize: '0.8rem' }
                                                            }
                                                        } 
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </td>
                                        <td className="p-2">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    value={item.end_date ? dayjs(item.end_date) : null}
                                                    onChange={(newValue) => {
                                                        handleChange(item.id, "end_date", newValue ? newValue.format('YYYY-MM-DD') : "");
                                                    }}
                                                    format="DD-MM-YYYY"
                                                    openTo="year"
                                                    views={['year', 'month', 'day']}
                                                    slotProps={{ 
                                                        textField: { 
                                                            size: "small",
                                                            sx: {
                                                                width: '150px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderRadius: '6px',
                                                                    '& fieldset': { border: 'none' },
                                                                },
                                                                '& .MuiInputBase-input': { padding: '6px 8px', fontSize: '0.8rem' }
                                                            }
                                                        } 
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </td>
                                        <td className="p-2">
                                            <Form.Select
                                                size="sm"
                                                value={item.payment_terms}
                                                onChange={(e) => handleChange(item.id, "payment_terms", e.target.value)}
                                                className="border-0 bg-light"
                                                style={{ borderRadius: '6px', padding: '8px 14px', fontSize: '0.85rem' }}
                                            >
                                                <option value="">Select Term</option>
                                                <option value="Daily">Daily</option>
                                                <option value="Monthly">Monthly</option>
                                                <option value="Quarterly">Quarterly</option>
                                                <option value="Yearly">Yearly</option>
                                            </Form.Select>
                                        </td>
                                        <td className="p-2">
                                            <Form.Select
                                                size="sm"
                                                value={item.type}
                                                onChange={(e) => handleChange(item.id, "type", e.target.value)}
                                                className="border-0 bg-light"
                                                style={{ borderRadius: '6px', padding: '8px 14px', fontSize: '0.85rem' }}
                                            >
                                                <option value="">Select</option>
                                                <option value="Subscription">Subscription</option>
                                                <option value="Deployment">Deployment</option>
                                            </Form.Select>
                                        </td>
                                        <td className="text-center p-2">
                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                <button
                                                    className="btn btn-sm btn-link text-primary p-0"
                                                    onClick={handleAddRow}
                                                    title="Add Item"
                                                    style={{ textDecoration: 'none' }}
                                                >
                                                    <i className="mdi mdi-plus-circle-outline fs-5"></i>
                                                </button>
                                                {unbundledItems.length > 1 && (
                                                    <button
                                                        className="btn btn-sm btn-link text-danger p-0"
                                                        onClick={() => handleRemoveRow(item.id)}
                                                        title="Remove Item"
                                                    >
                                                        <i className="mdi mdi-trash-can-outline fs-5"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-4 px-2">
                        <div className="text-end p-3 shadow-sm" style={{ background: '#f8f9fa', borderRadius: '8px', minWidth: '250px' }}>
                            <div className="text-muted small text-uppercase fw-bold mb-1">Total Unbundled</div>
                            <div className={`fs-4 fw-bold ${currentTotalUnbundled === totalInvoiceAmount ? "text-success" : "text-danger"}`}>
                                {currentTotalUnbundled.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                            </div>
                            {currentTotalUnbundled !== totalInvoiceAmount && (
                                <div className="text-danger small mt-1">
                                    Difference: {Math.abs(totalInvoiceAmount - currentTotalUnbundled).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>

            <div
                className="border-top px-4 py-3 d-flex justify-content-end align-items-center"
                style={{ background: '#f8f9fa' }}
            >
                <button
                    className="btn btn-outline-secondary me-2"
                    onClick={handleClose}
                    style={{ borderRadius: '6px', padding: '0.4rem 1.5rem', fontWeight: '500' }}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={loading || currentTotalUnbundled !== totalInvoiceAmount}
                    style={{ 
                        borderRadius: '6px', 
                        padding: '0.4rem 1.5rem', 
                        fontWeight: '500',
                        background: (loading || currentTotalUnbundled !== totalInvoiceAmount) ? '#a0b0ea' : '#4e73df',
                        border: 'none'
                    }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="mdi mdi-content-save-outline me-1"></i> Save Changes
                        </>
                    )}
                </button>
            </div>
        </Offcanvas>
    );
};

export default RevenueBundleModal;
